import { useState } from 'react';
import type { LiveUnits as LiveUnitsData, UnitState, WorkerUnit } from '../../../services/adminUnits';
import GraphToggle from './GraphToggle';
import Figure from './UnitFigure';
import UnitUsageGraph from './UnitUsageGraph';
import {
  endingsSummary,
  formatCores,
  formatCpuAveragePeak,
  formatGb,
  formatMemoryAveragePeak,
  spanUntil,
  unitsSummary,
} from './unitsFormat';

const StateChipClass: Record<UnitState, string> = {
  Starting: 'chip',
  Idle: 'chip ok',
  Busy: 'chip accent',
  Draining: 'chip warn',
};

function CurrentJob({ unit }: { unit: WorkerUnit }) {
  const job = unit.currentJob;
  if (!job) return <span className="muted">No job</span>;
  return (
    <span>
      {job.jobType == null
        ? 'A job whose row no longer exists'
        : `${job.jobType} job for ${job.projectName ?? 'a project that no longer exists'}`}
      {job.userEmail && <span className="muted"> ({job.userEmail})</span>}
      <span className="mono muted" style={{ fontSize: 12, marginLeft: 8 }}>{job.jobId}</span>
    </span>
  );
}

function HeartbeatFigure({ unit, asOf }: { unit: WorkerUnit; asOf: string }) {
  // The unit manager writes a unit's row at creation with both times equal; the unit's first heartbeat parts them.
  const beforeFirstHeartbeat = unit.state === 'Starting' && unit.lastHeartbeatAt === unit.startedAt;
  return beforeFirstHeartbeat ? (
    <Figure label="Created" value={`${spanUntil(unit.startedAt, asOf)} ago`} />
  ) : (
    <Figure label="Last heartbeat" value={`${spanUntil(unit.lastHeartbeatAt, asOf)} ago`} />
  );
}

function UnitCard({ unit, asOf }: { unit: WorkerUnit; asOf: string }) {
  const [graph, setGraph] = useState(false);
  return (
    <div className="card" role="listitem" aria-label={unit.unitId} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="mono" style={{ fontWeight: 600, fontSize: 13.5 }}>{unit.unitId}</span>
        <span className={StateChipClass[unit.state]}><span className="dot" />{unit.state}</span>
        {unit.drain && <span className="chip warn"><span className="dot" />Drain</span>}
        {unit.keepWarm && <span className="chip accent"><span className="dot" />Keep warm</span>}
        <GraphToggle shown={graph} onToggle={() => setGraph(!graph)} />
      </div>
      <div className="muted" style={{ fontSize: 12.5 }}>
        Group <span className="mono">{unit.groupName}</span> · {unit.region} · image{' '}
        <span className="mono">{unit.imageTag}</span> · {unit.cpuCores} cores, {unit.memoryGb} GB
      </div>
      <div style={{ fontSize: 13 }}><CurrentJob unit={unit} /></div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <Figure label="Jobs done" value={String(unit.jobsDone)} />
        <Figure label="Age" value={spanUntil(unit.startedAt, asOf)} />
        <HeartbeatFigure unit={unit} asOf={asOf} />
        <Figure label="Memory" value={formatGb(unit.memoryUsedBytes)} />
        <Figure label="Memory avg / peak" value={formatMemoryAveragePeak(unit.usage)} />
        <Figure label="CPU" value={formatCores(unit.cpuUsedCores)} />
        <Figure label="CPU avg / peak" value={formatCpuAveragePeak(unit.usage)} />
        <Figure label="Free disk" value={formatGb(unit.freeDiskBytes)} />
        <Figure label="Free disk min" value={formatGb(unit.usage?.freeDiskMinBytes ?? null)} />
      </div>
      {graph && <UnitUsageGraph unitId={unit.unitId} cpuCores={unit.cpuCores} memoryGb={unit.memoryGb} refreshKey={asOf} />}
    </div>
  );
}

export default function LiveUnits({ live, asOf }: { live: LiveUnitsData; asOf: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13 }}>
        <div style={{ fontWeight: 600 }}>{unitsSummary(live.units)}</div>
        <div className="muted">{endingsSummary(live.endedLastDay)}</div>
      </div>
      {live.units.length === 0 ? (
        <div className="card quiet" style={{ padding: 20 }}>
          <span className="muted">No unit is running.</span>
        </div>
      ) : (
        <div role="list" aria-label="Live units" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {live.units.map((unit) => (
            <UnitCard key={unit.unitId} unit={unit} asOf={asOf} />
          ))}
        </div>
      )}
    </div>
  );
}
